import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Box,
    Chip,
    CircularProgress,
    Alert,
    TextField,
    MenuItem,
    Grid,
    Card,
    CardContent,
    TablePagination
} from '@mui/material';
import { Sync as SyncIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import apiClient from '../services/apiClient';

interface Hospital {
    id: string;
    code: string;
    name: string;
    description: string;
    authority_id: string;
    type: string;
    enabled: boolean;
    active: boolean;
    disconnected: boolean;
    address: string;
    city: string;
    province: string;
    cap: string;
    phone_number: string;
    email: string;
    created_at: string;
    updated_at: string;
}

interface Authority {
    id: string;
    name: string;
}

const HospitalsPage: React.FC = () => {
    const [hospitals, setHospitals] = useState<Hospital[]>([]);
    const [authorities, setAuthorities] = useState<Authority[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [syncing, setSyncing] = useState(false);
    const [selectedAuthority, setSelectedAuthority] = useState('');
    
    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);

    const fetchHospitals = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                limit: rowsPerPage.toString(),
                offset: (page * rowsPerPage).toString()
            });
            
            if (selectedAuthority) {
                params.append('authorityId', selectedAuthority);
            }

            const response = await apiClient.get(`/recup-sync/hospitals?${params}`);
            if (response.data.success) {
                setHospitals(response.data.data);
                setTotalCount(response.data.pagination.total);
            } else {
                setError(response.data.error || 'Errore nel recupero degli hospitals');
            }
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Errore nel recupero degli hospitals');
        } finally {
            setLoading(false);
        }
    };

    const fetchAuthorities = async () => {
        try {
            const response = await apiClient.get('/recup-sync/authorities?limit=1000');
            if (response.data.success) {
                setAuthorities(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching authorities:', err);
        }
    };

    const handleSync = async () => {
        setSyncing(true);
        setError(null);
        try {
            const response = await apiClient.post('/recup-sync/sync/hospitals');
            if (response.data.success) {
                await fetchHospitals();
                alert(`Sincronizzazione completata con successo!\nCreati: ${response.data.created}\nAggiornati: ${response.data.updated}`);
            } else {
                setError(response.data.error || 'Errore nella sincronizzazione');
            }
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Errore nella sincronizzazione');
        } finally {
            setSyncing(false);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusChip = (hospital: Hospital) => {
        if (hospital.disconnected) {
            return <Chip label="Disconnesso" color="error" size="small" />;
        }
        if (!hospital.active) {
            return <Chip label="Inattivo" color="warning" size="small" />;
        }
        if (!hospital.enabled) {
            return <Chip label="Disabilitato" color="default" size="small" />;
        }
        return <Chip label="Attivo" color="success" size="small" />;
    };

    const getAuthorityName = (authorityId: string) => {
        const authority = authorities.find(a => a.id === authorityId);
        return authority?.name || authorityId;
    };

    useEffect(() => {
        fetchAuthorities();
    }, []);

    useEffect(() => {
        fetchHospitals();
    }, [page, rowsPerPage, selectedAuthority]);

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                Gestione Hospitals
            </Typography>

            {/* Statistiche */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Totale Hospitals
                            </Typography>
                            <Typography variant="h5">
                                {totalCount}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Hospitals Attivi
                            </Typography>
                            <Typography variant="h5">
                                {hospitals.filter(h => h.active && h.enabled && !h.disconnected).length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Authorities
                            </Typography>
                            <Typography variant="h5">
                                {authorities.length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Filtro Attivo
                            </Typography>
                            <Typography variant="h5">
                                {selectedAuthority ? 'Sì' : 'No'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Controlli */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        select
                        label="Filtra per Authority"
                        value={selectedAuthority}
                        onChange={(e) => {
                            setSelectedAuthority(e.target.value);
                            setPage(0);
                        }}
                        sx={{ minWidth: 200 }}
                        size="small"
                    >
                        <MenuItem value="">Tutte le authorities</MenuItem>
                        {authorities.map((authority) => (
                            <MenuItem key={authority.id} value={authority.id}>
                                {authority.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    
                    <Button
                        variant="outlined"
                        onClick={fetchHospitals}
                        disabled={loading}
                        startIcon={<RefreshIcon />}
                    >
                        Aggiorna
                    </Button>
                    
                    <Button
                        variant="contained"
                        onClick={handleSync}
                        disabled={syncing}
                        startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                        color="primary"
                    >
                        {syncing ? 'Sincronizzazione...' : 'Sincronizza da RECUP'}
                    </Button>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Tabella Hospitals */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Codice</TableCell>
                                <TableCell>Nome</TableCell>
                                <TableCell>Descrizione</TableCell>
                                <TableCell>Authority</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Stato</TableCell>
                                <TableCell>Ubicazione</TableCell>
                                <TableCell>Contatti</TableCell>
                                <TableCell>Creato</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell style={{ textAlign: 'center' }} colSpan={10}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : hospitals.length === 0 ? (
                                <TableRow>
                                    <TableCell style={{ textAlign: 'center' }} colSpan={10}>
                                        Nessun hospital trovato
                                    </TableCell>
                                </TableRow>
                            ) : (
                                hospitals.map((hospital) => (
                                    <TableRow key={hospital.id}>
                                        <TableCell>{hospital.id}</TableCell>
                                        <TableCell>{hospital.code}</TableCell>
                                        <TableCell>{hospital.name}</TableCell>
                                        <TableCell>{hospital.description}</TableCell>
                                        <TableCell>{getAuthorityName(hospital.authority_id)}</TableCell>
                                        <TableCell>{hospital.type}</TableCell>
                                        <TableCell>{getStatusChip(hospital)}</TableCell>
                                        <TableCell>
                                            {hospital.address && (
                                                <div>
                                                    <div>{hospital.address}</div>
                                                    <div>{hospital.city} {hospital.cap} ({hospital.province})</div>
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {(hospital.phone_number || hospital.email) && (
                                                <div>
                                                    {hospital.phone_number && <div>📞 {hospital.phone_number}</div>}
                                                    {hospital.email && <div>✉️ {hospital.email}</div>}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {new Date(hospital.created_at).toLocaleDateString()}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Righe per pagina:"
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
                    }
                />
            </Paper>
        </Container>
    );
};

export default HospitalsPage;